import React from 'react';

import { Slot } from 'radix-ui';
import { type VariantProps, cva } from 'class-variance-authority';
import { cn } from '~/components/ui/cn';

const buttonVariants = cva(
  'inline-flex items-center justify-center rounded-lg text-sm font-bold outline-none transition-colors duration-300 focus-visible:ring-2 focus-visible:ring-gray-900/30 disabled:cursor-not-allowed',
  {
    variants: {
      variant: {
        default:
          'border-2 border-gray-900 bg-gray-900 text-white hover:border-gray-700 hover:bg-gray-700 disabled:border-gray-300 disabled:bg-gray-300',
        outline:
          'border-2 border-gray-900 bg-white text-gray-900 hover:border-gray-700 hover:text-gray-700 disabled:border-gray-300 disabled:text-gray-300',
        destructive:
          'border border-red-600 bg-red-600 text-white hover:border-red-375 hover:bg-red-375 disabled:border-red-300 disabled:bg-red-300',
        link: 'text-green-900',
        ghost: 'disabled:text-gray-300',
      },
      size: {
        default:
          'has-data-[icon=inline-end]:pr-2 has-data-[icon=inline-start]:pl-2 h-10 gap-1.5 px-3.5',
        sm: "in-data-[slot=button-group]:rounded-lg has-data-[icon=inline-end]:pr-1.5 has-data-[icon=inline-start]:pl-1.5 h-8 gap-1 rounded px-2.5 text-xs [&_svg:not([class*='size-'])]:size-3.5",
        icon: 'size-10',
      },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
);

export type ButtonProps = React.ComponentProps<'button'> &
  VariantProps<typeof buttonVariants> & {
    asChild?: boolean;
  };

const Button: React.FC<ButtonProps> = ({
  className,
  variant = 'default',
  size = 'default',
  asChild = false,
  ...props
}) => {
  const Comp = asChild ? Slot.Root : 'button';

  return (
    <Comp
      data-slot="button"
      data-variant={variant}
      data-size={size}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    />
  );
};

Button.displayName = 'Button';

export { Button };
